import {
    CameraWithInfo,
    ContraventionReasonTranslations,
    GeoZoneCoordinates,
    GeoZoneOperationConfigWithGracePeriod,
    GeoZoneValidationType,
    GeoZones,
    Status,
    Location,
} from '@EcamModel/model';
import { GeoZoneOperationConfigGracePeriodWithDay } from '@EcamModel/model/GeoZoneOperationConfigGracePeriod';
import IcMenu from '@assets/details-camera-icons/IcMenu';
import IcDocument from '@assets/jsx-icon/IcDocument';
import IcDocumentFilter from '@assets/jsx-icon/IcDocumentFilter';
import FramePolygonSized, { isClosedPolygon } from '@components/FramePolygonSized';
import IOSSwitch from '@components/IOSSwitch';
import BreadCrumbs, { IBreadCrumbs } from '@components/breadcumbs/BreadCumbs';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { pushSuccess } from '@components/toast';
import {
    cameraController,
    geoZoneController,
    geoZoneOperationConfigController,
    locationController,
} from '@controllers/index';
import { yupResolver } from '@hookform/resolvers/yup';
import usePopUp from '@hooks/usePopUp';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Fade,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import NoDataCameras from '@pages/cameras/list/components/NoDataCameras';
import { convertSecondsToDate } from '@utils/functions';
import { formatSecondsToHHMMSS } from '../utils';
import { groupBy } from 'lodash';
import moment from 'moment';
import { formatDateByTimeZone, formatUKDate } from 'src/helpers';
import { useEffect, useState, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BsQuestion } from 'react-icons/bs';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { Mode } from '..';
import PopUpEditablePeriods from './PopUpEditablePeriods';
import PopUpWarning from '@components/PopUpWarning';
import BaseCheckbox from '@components/BaseCheckbox';
import { MdOutlineArrowDropUp } from 'react-icons/md';
import { useQueryClient } from 'react-query';
import { BaseTextField } from '@components/BaseTextField';
import { renderDetectModesOption } from './tabsGeozonesDetail/ptz/upsertGeoZones/components/EditGeoZonesPTZ';
import { Zones } from '@EcamModel/model/Zones';
import { Paging } from '@Core';

export type DetectMode = {
    Label: string;
    Value: GeoZoneValidationType;
    Note?: string;
};
export type FormValues = {
    contraventionReasonTranslations: ContraventionReasonTranslations;
    detectMode: DetectMode;
    isActive: boolean;
    geoZonePoints: GeoZoneCoordinates[];
    isExemptVRNs: boolean;
    geoZoneName: string;
    totalBay: number;
    location?: Location;
    zone?: Zones;
};

export const schema: any = yup.object().shape({
    contraventionReasonTranslations: yup.object().required(),
});
export const detectModes: DetectMode[] = [
    {
        Label: 'Contained',
        Value: GeoZoneValidationType.Contained,
        Note: 'Only one/some of 4 coordinates of the plate number with in the geo zone we can understand the vehicle within the geo-zone.',
    },
    {
        Label: 'Center',
        Value: GeoZoneValidationType.Center,
        Note: 'The center point of the plate number must be within the geo-zone.',
    },
];

export default function EditGeoZones() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const popUpEditablePeriods = usePopUp();
    const popUpDeletePeriods = usePopUp();
    const params = useParams<{ id: string; geoZoneId: string }>();

    // const { selectedGeoZone } = location.state || {};

    const [camera, setCamera] = useState<CameraWithInfo>({} as CameraWithInfo);
    const [selectedGeoZone, setSelectedGeoZone] = useState<GeoZones>({} as GeoZones);
    const [loading, setLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const [contraventionReasons, setContraventionReasons] = useState<ContraventionReasonTranslations[]>([]);
    const [mode, setMode] = useState<Mode>('create');
    const [selectedTab, setSelectedTab] = useState(1);
    const [zones, setZones] = useState<Zones[]>([]);

    const [selectedScheduleOperationHours, setSelectedScheduleOperationHours] = useState<number | undefined>(undefined);

    const [scheduleOperationConfigs, setScheduleOperationConfigs] = useState<GeoZoneOperationConfigWithGracePeriod[]>(
        []
    );

    const breadcrumbs: IBreadCrumbs[] = [
        { title: 'Camera list', href: `/cameras` },
        { title: `${camera.Name}`, href: `/cameras/${camera.Id}` },
        { title: `Edit geo-zones`, isColorBlackText: true },
    ];

    const currentGeoZoneConfigs = selectedGeoZone.GZOConfigs?.[0];

    const currentOperationHours = currentGeoZoneConfigs?.GZOConfigGracePeriods.flatMap((item) =>
        item.GZOConfigDays.map((item) => item)
    ).filter((item) => !!item.Status);
    const _currentOperationHours = groupBy(currentOperationHours, 'Weekday');

    const currentSelectedPeriodConfigs = selectedGeoZone.GZOConfigs?.[0]?.GZOConfigGracePeriods;

    const nextGeoZoneConfigs = scheduleOperationConfigs.find((item) => item.Id === selectedScheduleOperationHours!);

    const nextOperationHours = nextGeoZoneConfigs?.GZOConfigGracePeriods.flatMap((item) =>
        item.GZOConfigDays.map((item) => item)
    ).filter((item) => !!item.Status);

    const nextPeriodConfigs = nextGeoZoneConfigs?.GZOConfigGracePeriods;

    const _nextOperationHours = groupBy(nextOperationHours, 'Weekday');

    const isEdit = moment(nextGeoZoneConfigs?.EffectiveFrom).startOf('date').isAfter(moment().startOf('date'));

    const {
        watch,
        reset,
        control: _control,
        setValue: _setValue,
        handleSubmit,
        formState: { isDirty, isValid, errors, ...formState },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {
            contraventionReasonTranslations: undefined,
            detectMode: detectModes[0],
            isActive: true,
            geoZonePoints: [],
            isExemptVRNs: false,
            geoZoneName: '',
            location: undefined,
            zone: undefined,
        },
    });

    const setValue = _setValue as (name: string, value: any, options?: Record<string, boolean>) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const control = _control as any;

    const blobName = camera.CameraPhotos?.[0]?.BlobName;

    const prevGeoZonePoints: GeoZoneCoordinates[][] = camera.GeoZones?.map((gz) => {
        return gz.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index).concat(gz.GeoZoneCoordinates[0]);
    }) as GeoZoneCoordinates[][];

    const filterPreGeoZones = prevGeoZonePoints?.map((item) =>
        JSON.stringify(item) !==
        JSON.stringify(
            selectedGeoZone.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index)?.concat(
                selectedGeoZone.GeoZoneCoordinates[0]
            )
        )
            ? item
            : [
                  { Index: 1, XPoint: undefined, YPoint: undefined, GeoZoneId: selectedGeoZone.Id },
                  { Index: 2, XPoint: undefined, YPoint: undefined, GeoZoneId: selectedGeoZone.Id },
                  { Index: 3, XPoint: undefined, YPoint: undefined, GeoZoneId: selectedGeoZone.Id },
                  { Index: 4, XPoint: undefined, YPoint: undefined, GeoZoneId: selectedGeoZone.Id },
              ]
    );

    const geoZonePoints = watch().geoZonePoints;

    const contraventionAutocomplete = useStyledAutocomplete({
        list: {
            options: contraventionReasons,
            isFiltered(option, searchText) {
                return option.Summary.toLowerCase().includes(searchText.toLowerCase());
            },
        },
        dependencyList: [contraventionReasons],
    });
    const detectModeAutocomplete = useStyledAutocomplete({
        list: {
            options: detectModes,
            isFiltered(option, searchText) {
                return option.Label.toLowerCase().includes(searchText.toLowerCase());
            },
        },
    });

    const availableLocations = useMemo(() => {
        const locationMap = new Map<number, Location>();

        if (camera.Zone?.Location) {
            const primaryLoc = camera.Zone.Location;
            locationMap.set(primaryLoc.Id!, {
                ...primaryLoc,
                Zones: [camera.Zone],
            });
        }

        if (camera.ExtendedCoverage && Array.isArray(camera.ExtendedCoverage)) {
            (camera.ExtendedCoverage as Location[]).forEach((extLoc) => {
                const existingLoc = locationMap.get(extLoc.Id!);

                if (existingLoc) {
                    const mergedZones = [...(existingLoc.Zones || [])];
                    (extLoc.Zones || []).forEach((zone) => {
                        if (!mergedZones.some((z) => z.Id === zone.Id)) {
                            mergedZones.push(zone);
                        }
                    });
                    locationMap.set(extLoc.Id!, {
                        ...extLoc,
                        Zones: mergedZones,
                    });
                } else {
                    locationMap.set(extLoc.Id!, extLoc);
                }
            });
        }

        return Array.from(locationMap.values());
    }, [camera.Zone, camera.ExtendedCoverage]);

    const locationAutocomplete = useStyledAutocomplete({
        list: {
            options: availableLocations,
            isFiltered(option: Location, searchText) {
                return option.Name.toLowerCase().includes(searchText.toLowerCase());
            },
        },
        dependencyList: [availableLocations],
    });

    const zoneAutocomplete = useStyledAutocomplete({
        list: {
            options: zones,
            isFiltered(option, searchText) {
                return option.PublicName.toLowerCase().includes(searchText.toLowerCase());
            },
        },
        dependencyList: [zones],
    });

    const handleEditGeoZone = (data: FormValues) => {
        setIsLoading(true);
        const _geoZones: GeoZones = {
            Id: selectedGeoZone.Id,
            CameraId: camera.Id!,
            ContraventionReasonId: Number(data.contraventionReasonTranslations.Id!),
            Status: !!data.isActive ? Status.Active : Status.Inactive,
            ValidationType: data.detectMode.Value,
            GeoZoneName: data.geoZoneName.trim() || undefined,
            GeoZoneCoordinates: data.geoZonePoints.slice(0, data.geoZonePoints.length - 1).map((item) => ({
                GeoZoneId: item.GeoZoneId,
                Index: item.Index,
                XPoint: item.XPoint,
                YPoint: item.YPoint,
            })),
            PermitExemptionsEnabled: data.isExemptVRNs,
            ZoneId: data.zone?.Id,
        };
        geoZoneController
            .upsert(_geoZones)
            .then((res) => {
                queryClient.invalidateQueries(['trailAuditEvent']);
                navigate(`/cameras/${camera.Id}`, { state: { tabValue: 1 } });
            })
            .finally(() => {
                pushSuccess('Edit geo-zones successfully');
                setIsLoading(false);
            });
    };
    const handleConfirm = async (data: GeoZoneOperationConfigWithGracePeriod) => {
        setIsLoading(true);
        await geoZoneOperationConfigController
            .upsertGzoConfig(data)
            .then(async (res) => {
                setScheduleOperationConfigs(res);
                await geoZoneController.get(params.geoZoneId!).then((res) => setSelectedGeoZone(res));
                popUpEditablePeriods.onClose();
            })
            .finally(() => {
                pushSuccess('Update configs successfully');
                setIsLoading(false);
            });
    };

    const handleDelete = async (id: number) => {
        setIsLoading(true);
        await geoZoneOperationConfigController
            .deleteGzoConfig(id)
            .then(async (res) => {
                setScheduleOperationConfigs(res);
                setSelectedScheduleOperationHours(res.at(0)?.Id || -1);
                await geoZoneController.get(params.geoZoneId!).then((res) => setSelectedGeoZone(res));
                popUpDeletePeriods.onClose();
            })
            .finally(() => {
                setIsLoading(false);
                pushSuccess('Delete configs successfully');
            });
    };
    useEffect(() => {
        geoZoneController.getContraventionReasonTranslations().then((res) => {
            setContraventionReasons(res);
        });

        const _detectMode = detectModes.find((m) => m.Value === selectedGeoZone.ValidationType);
        reset({
            contraventionReasonTranslations: selectedGeoZone.ContraventionReason?.ContraventionReasonTranslations?.[0],
            detectMode: _detectMode,
            isActive: Boolean(selectedGeoZone.Status === Status.Active),
            isExemptVRNs: Boolean(selectedGeoZone.PermitExemptionsEnabled),
            geoZonePoints: selectedGeoZone.GeoZoneCoordinates,
            geoZoneName: selectedGeoZone.GeoZoneName || '',
        });
    }, [
        reset,
        selectedGeoZone.ContraventionReason?.ContraventionReasonTranslations,
        selectedGeoZone.GeoZoneCoordinates,
        selectedGeoZone.Status,
        selectedGeoZone.PermitExemptionsEnabled,
        selectedGeoZone.ValidationType,
        selectedGeoZone?.GeoZoneName,
    ]);

    useEffect(() => {
        const zoneToUse = selectedGeoZone.Zone || camera.Zone;

        if (zoneToUse?.Location && availableLocations.length > 0) {
            const mergedLocation = availableLocations.find((loc) => loc.Id === zoneToUse.Location?.Id);

            if (mergedLocation) {
                setValue('location', mergedLocation);
                setZones(mergedLocation.Zones || []);

                const currentZone = mergedLocation.Zones?.find((z) => z.Id === zoneToUse.Id);
                setValue('zone', currentZone || zoneToUse);
            }
        }
    }, [selectedGeoZone.Zone, camera.Zone, availableLocations, setValue]);

    useEffect(() => {
        setLoading(true);
        const init = async () => {
            await cameraController
                .get(params.id!)
                .then((res) => setCamera(res))
                .catch((err) => {
                    navigate('/cameras');
                });
            await geoZoneController.get(params.geoZoneId!).then((res) => setSelectedGeoZone(res));
            const geoZoneId = parseInt(params.geoZoneId!);
            await geoZoneOperationConfigController.getPastOrFuturePeriods(geoZoneId).then((res) => {
                setScheduleOperationConfigs(res);
                setSelectedScheduleOperationHours(res.at(0)?.Id || -1);
            });
            setLoading(false);
        };
        init();
    }, [params.geoZoneId, params.id]);

    return (
        <Box>
            {loading ? (
                <Stack direction="row" justifyContent="center" alignItems="center" minHeight={500}>
                    <CircularProgress />
                </Stack>
            ) : (
                <Stack mt={1}>
                    <Stack mb={3}>
                        <Typography variant="h3">{'Edit geo-zones'}</Typography>
                        <BreadCrumbs breadcrumbs={breadcrumbs} />
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Controller
                            name="contraventionReasonTranslations"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <StyledAutocomplete
                                    {...field}
                                    {...contraventionAutocomplete}
                                    isRequired
                                    getOptionLabel={(o) => o.Summary}
                                    label="Contravention reason"
                                    value={field.value}
                                    placeholder="Select reason"
                                    disabledAllOption
                                    onChange={(c) => {
                                        setValue('contraventionReasonTranslations', c!, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    wrapperWidth="100%"
                                />
                            )}
                        />
                        <Controller
                            name="detectMode"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <StyledAutocomplete
                                    {...field}
                                    {...detectModeAutocomplete}
                                    isRequired
                                    getOptionLabel={(o) => o.Label}
                                    customRenderOption={(_, option) => renderDetectModesOption(option, detectModes)}
                                    label="Detect mode"
                                    value={field.value}
                                    disabledAllOption
                                    onChange={(c) => {
                                        field.onChange(c);
                                    }}
                                    wrapperWidth="100%"
                                />
                            )}
                        />
                        <div>
                            <Controller
                                name="isActive"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <IOSSwitch
                                        {...field}
                                        label={
                                            <Typography sx={{ minWidth: '55px' }}>
                                                {!!field.value ? 'Active' : 'Inactive'}
                                            </Typography>
                                        }
                                        iosSwitchProps={{
                                            checked: field.value,
                                            onChange(event, checked) {
                                                field.onChange(event);
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Stack>

                    {/*  geozone name */}
                    <Stack direction="row" alignItems="center" justifyContent="start" spacing={2} mt={2}>
                        <Controller
                            name="geoZoneName"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <BaseTextField
                                    {...field}
                                    label="Geozone name"
                                    isVariantOutlined
                                    placeholder="Enter geozone name"
                                    maxLength={50}
                                    error={!!error?.message}
                                    helperText={error?.message}
                                    sx={{ width: '100%' }}
                                />
                            )}
                        />
                    </Stack>

                    {/* Location and Zone */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mt={2}>
                        <Controller
                            name="location"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <StyledAutocomplete
                                    {...field}
                                    {...locationAutocomplete}
                                    getOptionLabel={(o) => (o as Location)?.Name}
                                    label="Location"
                                    value={field.value}
                                    placeholder="Select location"
                                    disabledAllOption
                                    onChange={(l: Location) => {
                                        setValue('location', l!, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });
                                        setValue('zone', undefined as any);
                                        setZones(l?.Zones || []);
                                        if (l?.Zones && l.Zones.length > 0) {
                                            setValue('zone', l.Zones[0]);
                                        }
                                    }}
                                    wrapperWidth="100%"
                                />
                            )}
                        />
                        <Controller
                            name="zone"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <StyledAutocomplete
                                    {...field}
                                    {...zoneAutocomplete}
                                    getOptionLabel={(o) => `${(o as Zones)?.PublicName} (ZoneID ${(o as Zones)?.Id})`}
                                    label="Zone"
                                    value={field.value}
                                    placeholder="Select zone"
                                    disabledAllOption
                                    disabled={!watch().location}
                                    onChange={(z: Zones) => {
                                        setValue('zone', z!, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    wrapperWidth="100%"
                                />
                            )}
                        />
                    </Stack>

                    {/* checkbox ExemptVRNs */}
                    <Stack direction="row" alignItems="center" justifyContent={'space-between'} width="100%" mt={2}>
                        <Stack direction="row" alignItems="center">
                            <Controller
                                name="isExemptVRNs"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <FormControlLabel
                                        control={
                                            <BaseCheckbox
                                                checked={field.value}
                                                onChange={(_, checked) => {
                                                    field.onChange(checked);
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography ml={0.5} mt={0.3}>
                                                Exempt VRNs on Permit lists
                                            </Typography>
                                        }
                                        sx={{ ml: 0 }}
                                    />
                                )}
                            />
                        </Stack>
                    </Stack>
                    {/* end checkbox ExemptVRNs  */}

                    {/* Draw angle box */}
                    <HeadingSection icon={<IcMenu />} heading="Zone marking" />
                    <div style={{ marginBottom: '51px' }} />
                    <Controller
                        name="geoZonePoints"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <FramePolygonSized
                                {...field}
                                prevGeoZone={filterPreGeoZones as GeoZoneCoordinates[][]}
                                selectedGeoZone={selectedGeoZone}
                                onChange={(points) => {
                                    setValue('geoZonePoints', points, {
                                        shouldValidate: true,
                                        shouldTouch: true,
                                        shouldDirty: true,
                                    });
                                }}
                                blobName={blobName}
                                mode={'edit'}
                            />
                        )}
                    />
                    {/* Operational periods */}
                    <Stack mt={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {[
                                { index: 1, title: 'Current Operational Period', icon: <IcDocument /> },
                                {
                                    index: 2,
                                    title: `Past/Future Periods (${scheduleOperationConfigs?.length})`,
                                    icon: <IcDocumentFilter />,
                                },
                            ].map((item) => {
                                return (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        sx={{
                                            py: 1,
                                            px: 2,
                                            background: selectedTab === item.index ? '#E8F5E9' : '#FAFAFA',
                                            borderRadius: '8px 8px 0px 0px',
                                            cursor: 'pointer',
                                            transition: '.2s ease-in-out',
                                        }}
                                        onClick={() => {
                                            setSelectedTab(item.index);
                                        }}
                                        spacing={1}
                                    >
                                        {item.icon && (
                                            <Box
                                                width={16}
                                                height={16}
                                                color={selectedTab === item.index ? '#009D4F' : '#85858A'}
                                            >
                                                {item.icon}
                                            </Box>
                                        )}
                                        <Typography color={selectedTab === item.index ? '#009D4F' : '#85858A'}>
                                            {item.title}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                        <Stack
                            sx={{
                                p: 2,
                                border: '1px solid #DDDDDD',
                                borderRadius: '0px 8px 8px 8px',
                            }}
                        >
                            {selectedTab === 1 && (
                                <Stack>
                                    {!!currentGeoZoneConfigs?.EffectiveFrom && (
                                        <Stack
                                            sx={{
                                                borderBottom: '1px solid #DDDDDD',
                                                pb: 1,
                                            }}
                                        >
                                            <Typography variant="subtitle2" fontWeight={500}>
                                                Effective from:{' '}
                                                {formatUKDate(currentGeoZoneConfigs?.EffectiveFrom)}
                                            </Typography>
                                            <OperationHourHistory
                                                createdBy={currentGeoZoneConfigs?.CreatedBy!}
                                                createdAt={formatDateByTimeZone(
                                                    currentGeoZoneConfigs?.Created,
                                                    undefined,
                                                    false
                                                )}
                                                updatedBy={currentGeoZoneConfigs?.UpdatedBy!}
                                                updatedAt={formatDateByTimeZone(
                                                    currentGeoZoneConfigs?.Updated,
                                                    undefined,
                                                    false
                                                )}
                                            />
                                        </Stack>
                                    )}
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        mt={!!currentGeoZoneConfigs?.EffectiveFrom ? 2 : 0}
                                    >
                                        <Typography fontWeight={500}>Operation hours</Typography>
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                setMode('create');
                                                popUpEditablePeriods.setTrue();
                                            }}
                                        >
                                            Create new
                                        </Button>
                                    </Stack>
                                    {!!currentOperationHours?.length ? (
                                        <Stack spacing={1} mt={2}>
                                            {Object.entries(_currentOperationHours).map(([key, value], index) => {
                                                return (
                                                    <OperationHourSection
                                                        weekday={parseInt(key)}
                                                        operationHours={value}
                                                        gracePeriods={currentSelectedPeriodConfigs}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    ) : (
                                        <Box mt={2}>
                                            <NoDataCameras />
                                        </Box>
                                    )}
                                </Stack>
                            )}
                            {selectedTab === 2 && (
                                <Stack>
                                    <Stack
                                        sx={{
                                            borderBottom: '1px solid #DDDDDD',
                                            pb: 1,
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                                            <Typography variant="subtitle2" fontWeight={500}>
                                                Effective from:
                                            </Typography>
                                            <FormControl sx={{ width: '70%' }}>
                                                <Select
                                                    displayEmpty
                                                    inputProps={{ 'aria-label': 'Without label' }}
                                                    value={selectedScheduleOperationHours || -1}
                                                    onChange={(e) => {
                                                        setSelectedScheduleOperationHours(e.target.value as any);
                                                    }}
                                                >
                                                    <MenuItem value={-1}>Please select an option</MenuItem>
                                                    {scheduleOperationConfigs.map((item) => {
                                                        return (
                                                            <MenuItem value={item.Id}>
                                                                {formatUKDate(item.EffectiveFrom)}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                        {selectedScheduleOperationHours !== -1 && (
                                            <Fade in={!!selectedScheduleOperationHours}>
                                                <Box>
                                                    <OperationHourHistory
                                                        createdBy={nextGeoZoneConfigs?.CreatedBy!}
                                                        createdAt={formatDateByTimeZone(
                                                            nextGeoZoneConfigs?.Created,
                                                            undefined,
                                                            false
                                                        )}
                                                        updatedBy={nextGeoZoneConfigs?.UpdatedBy!}
                                                        updatedAt={formatDateByTimeZone(
                                                            nextGeoZoneConfigs?.Updated,
                                                            undefined,
                                                            false
                                                        )}
                                                    />
                                                </Box>
                                            </Fade>
                                        )}
                                    </Stack>
                                    {selectedScheduleOperationHours !== -1 ? (
                                        <>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                mt={2}
                                            >
                                                <Typography fontWeight={500}>Operation hours</Typography>
                                                <Fade in={!!isEdit}>
                                                    <Stack direction="row" alignItems="center" spacing={2}>
                                                        <Button
                                                            variant="errorContained"
                                                            onClick={() => {
                                                                popUpDeletePeriods.setTrue();
                                                            }}
                                                            sx={{ minWidth: 100 }}
                                                        >
                                                            Delete
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            onClick={() => {
                                                                setMode('edit');
                                                                popUpEditablePeriods.setTrue();
                                                            }}
                                                            sx={{ minWidth: 100 }}
                                                        >
                                                            Edit
                                                        </Button>
                                                    </Stack>
                                                </Fade>
                                            </Stack>
                                            {!!nextOperationHours?.length ? (
                                                <Stack spacing={1} mt={2}>
                                                    {Object.entries(_nextOperationHours).map(([key, value], index) => {
                                                        return (
                                                            <OperationHourSection
                                                                weekday={parseInt(key)}
                                                                operationHours={value}
                                                                gracePeriods={nextPeriodConfigs}
                                                            />
                                                        );
                                                    })}
                                                </Stack>
                                            ) : (
                                                <Box mt={2}>
                                                    <NoDataCameras />
                                                </Box>
                                            )}
                                        </>
                                    ) : (
                                        <Box mt={2}>
                                            <NoDataCameras />
                                        </Box>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                    <PopUpEditablePeriods
                        {...popUpEditablePeriods}
                        onClose={popUpEditablePeriods.onClose}
                        mode={mode}
                        selectedGeoZone={selectedGeoZone}
                        gracePeriodConfigs={nextPeriodConfigs as any}
                        effectiveFrom={nextGeoZoneConfigs?.EffectiveFrom}
                        selectedGeoZoneConfigsId={nextGeoZoneConfigs?.Id!}
                        onConfirm={handleConfirm}
                        isLoading={isLoading}
                        contraventionReasonId={watch()?.contraventionReasonTranslations?.ContraventionReasonId}
                    />
                    <PopUpWarning
                        {...popUpDeletePeriods}
                        onConfirm={() => {
                            handleDelete(nextGeoZoneConfigs?.Id!);
                        }}
                        message="Are you sure you want to delete this operation period?"
                        isLoading={isLoading}
                    />
                    <Stack mt={3} direction={'row'} width="100%" justifyContent="space-between">
                        <Button
                            sx={{ minWidth: 130 }}
                            variant="cancel"
                            onClick={() => {
                                navigate(`/cameras/${camera.Id}`);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            sx={{ minWidth: 130 }}
                            variant="contained"
                            onClick={handleSubmit(handleEditGeoZone)}
                            disabled={
                                !(isValid && isDirty) ||
                                isLoading ||
                                !isClosedPolygon(geoZonePoints) ||
                                !watch().detectMode
                            }
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            )}
        </Box>
    );
}
const HeadingSection = ({ icon, heading }: { icon: React.ReactNode; heading: string }) => {
    return (
        <Box
            sx={{
                p: 2,
                background: '#FAFAFA',
                borderBottom: '1px solid #DDDDDD',
                borderRadius: '4px 4px 0px 0px',
                mt: 4,
                userSelect: 'none',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1}>
                {icon}
                <Typography variant="h5">{heading}</Typography>
            </Stack>
        </Box>
    );
};

export const OperationHourSection = ({
    weekday,
    operationHours,
    gracePeriods,
}: {
    weekday: number;
    operationHours: any[];
    gracePeriods?: GeoZoneOperationConfigGracePeriodWithDay[];
}) => {
    return (
        <Stack direction="row" sx={{ p: 2, border: '1px solid #DDDDDD', borderRadius: 1 }} alignItems="center">
            <Typography pr={4} minWidth={110}>
                {moment().isoWeekday(weekday).format('dddd')}
            </Typography>
            {operationHours.map((o) => {
                const periodConfigs = gracePeriods?.find((p) => p.Id === o.GeoZoneOperationConfigGracePeriodId);
                return (
                    <Stack direction="row" alignItems="center" spacing={0.5} pr={2}>
                        <Box
                            sx={{
                                p: 0.4,
                                background: '#256428',
                                borderRadius: 1,
                            }}
                        />
                        <Typography color="GrayText">
                            {moment(convertSecondsToDate(o.From)).format('HH:mm')} -{' '}
                            {moment(convertSecondsToDate(o.To)).format('HH:mm')} | Grace period:{' '}
                            {periodConfigs?.GracePeriod != null ? formatSecondsToHHMMSS(periodConfigs.GracePeriod) : '-'}
                        </Typography>
                    </Stack>
                );
            })}
        </Stack>
    );
};

export const OperationHourHistory = ({
    createdBy,
    createdAt,
    updatedBy,
    updatedAt,
}: {
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
}) => {
    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>
                Created by {createdBy} at {createdAt}
            </Typography>
            <Divider orientation="vertical" variant="middle" flexItem />
            <Typography>
                Updated by {updatedBy} on {updatedAt}
            </Typography>
        </Stack>
    );
};
